import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { getFlowOSServices, getPlans, revertPlanEntitlements, savePlanEntitlements } from "../../actions";
import { canPlatform } from "../../utils/permissions";
import { brand, notice } from "../../theme/businessFlowsTheme";
import { Hint, Refusal, Surface, Toast, dialogPaper, focusRing, menuPaper, quietDanger } from "../workspaces/WorkspaceUi";

// What each entitlement means, in the order it is shown, and how an admin
// changes it.
const ROWS = [
  { key: "content.scope", label: "Content", kind: "choice", options: [
    { value: "sample", label: "Sample processes" },
    { value: "full", label: "Full library" },
  ] },
  { key: "content.ownDatabases", label: "Own databases", kind: "number", format: (v) => v || "None" },
  { key: "seats", label: "Seats included", kind: "number", format: (v) => v || 0 },
  { key: "feature.relationshipExplorer", label: "Relationship Explorer", kind: "flag" },
  { key: "feature.advancedSearch", label: "Advanced search", kind: "flag" },
  { key: "feature.watermark", label: "Watermark on diagrams", kind: "flag" },
  { key: "export.svg", label: "Export SVG", kind: "flag" },
  { key: "export.bpmn", label: "Export BPMN", kind: "flag" },
  { key: "import.bpmn", label: "Import BPMN", kind: "flag" },
  { key: "flowos.services", label: "FlowOS services", kind: "services", format: (v) => (v?.length ? v.join(", ") : "None") },
  { key: "flowos.monthlyCredits", label: "FlowOS credits per month", kind: "number", format: (v) => v || 0 },
  { key: "integrations.auraConnect", label: "Connect AuraDB", kind: "flag" },
  { key: "integrations.mcp", label: "MCP integrations", kind: "flag" },
];

const Flag = ({ on }) =>
  on ? (
    <CheckRoundedIcon sx={{ fontSize: 18, color: notice.good.fg }} titleAccess="Included" />
  ) : (
    <CloseRoundedIcon sx={{ fontSize: 18, color: "text.disabled" }} titleAccess="Not included" />
  );

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export default function PlansEntitlements({ user = {} }) {
  const mayManage = canPlatform(user, "subscriptions.manage");
  const [plans, setPlans] = useState(null);
  // Whether a plan is allowed to refuse anything at all. Off since 28 Sep 2026
  // (backend/services/planEnforcement.js): access comes from Views and the
  // group that grants a database, so this screen is a billing record until
  // someone turns plans back on.
  const [enforced, setEnforced] = useState(true);
  const [services, setServices] = useState({ services: [], source: "bfb" });
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    const res = await getPlans();
    if (res.success) {
      setPlans(res.content.plans);
      setEnforced(Boolean(res.content.enforced));
    } else {
      setPlans([]);
      setError(res.msg || "Couldn't load the plans.");
    }
  }, []);

  useEffect(() => {
    load();
    getFlowOSServices().then((res) => {
      if (res?.success) setServices(res.content);
    });
  }, [load]);

  const changedCount = useMemo(
    () => (plans || []).reduce((total, plan) => total + Object.keys(plan.changes || {}).length, 0),
    [plans]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, ...focusRing }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "flex-start" }} sx={{ mb: 2 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: brand.ink, color: brand.signal }}>
          <WorkspacePremiumRoundedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Plans and entitlements
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 760 }}>
            What each plan unlocks, for every workspace on it. Assign a plan to a workspace under Workspaces, where a single
            workspace can also be granted FlowOS on its own.
          </Typography>
        </Box>
        {changedCount > 0 && (
          <Chip
            size="small"
            icon={<AutoAwesomeRoundedIcon />}
            label={`${changedCount} changed here`}
            sx={{ bgcolor: brand.mark, border: `1px solid ${brand.signalDeep}`, color: brand.ink, fontWeight: 700 }}
          />
        )}
      </Stack>

      {!enforced && (
        <Hint
          sx={{ mb: 2 }}
          next="To make plans decide again, set “Subscription plans decide access” to on under Platform settings → Plans and access."
        >
          <strong>Plans don’t decide access.</strong> What a person may open, and what they see in it, is the view they hold
          on a database; what they may change is that view’s edit mode and their workspace role. Nothing on this page
          refuses anything today — no seat limit, no trial expiry, no “not in your plan”. It is kept, and still saved,
          because it says what each customer is billed for.
        </Hint>
      )}

      {!mayManage && (
        <Hint sx={{ mb: 2 }} next="Changing what a plan unlocks is for platform admins.">
          You can review every plan and what it includes.
        </Hint>
      )}
      {error && (
        <Refusal sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Refusal>
      )}

      {plans === null ? (
        <Skeleton variant="rounded" height={360} sx={{ borderRadius: 4 }} />
      ) : (
        <Surface>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 680 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Included</TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.key} align="center">
                      <Stack spacing={0.5} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {plan.name}
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {plan.trialDays ? (
                            <Chip size="small" label={`${plan.trialDays} days`} sx={{ height: 20, bgcolor: brand.shell, color: brand.muted }} />
                          ) : null}
                          {mayManage && (
                            <Tooltip title={`Change what ${plan.name} unlocks`}>
                              <IconButton size="small" onClick={() => setEditing(plan.key)} aria-label={`Change ${plan.name}`}>
                                <EditRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Stack>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {ROWS.map((row) => (
                  <TableRow key={row.key} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
                    {plans.map((plan) => {
                      const value = plan.entitlements?.[row.key];
                      const change = plan.changes?.[row.key];
                      return (
                        <TableCell key={plan.key} align="center" sx={change ? { bgcolor: brand.markSoft } : undefined}>
                          <Tooltip
                            title={
                              change
                                ? `Changed here${change.updatedBy ? ` by ${change.updatedBy}` : ""}. Shipped default: ${
                                    row.format ? row.format(change.default) : change.default ? "included" : "not included"
                                  }`
                                : ""
                            }
                          >
                            <span>
                              {row.kind === "flag" ? <Flag on={Boolean(value)} /> : <Typography variant="body2">{row.format ? row.format(value) : String(value)}</Typography>}
                            </span>
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Surface>
      )}

      <PlanDialog
        plan={(plans || []).find((entry) => entry.key === editing) || null}
        services={services}
        onClose={() => setEditing(null)}
        onSaved={async (message) => {
          setEditing(null);
          setToast(message);
          await load();
        }}
        onError={setError}
      />

      <Toast notice={toast} onClose={() => setToast("")} />
    </Box>
  );
}

// One plan's entitlements. Saving applies to every tenant on the plan, which
// the dialog says plainly.
function PlanDialog({ plan, services, onClose, onSaved, onError }) {
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (plan) setDraft({ ...plan.entitlements });
  }, [plan]);

  if (!plan) return null;

  const dirty = ROWS.some((row) => !same(draft[row.key], plan.entitlements?.[row.key]));
  const changedHere = Object.keys(plan.changes || {}).length;
  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));

  const save = async () => {
    setBusy("save");
    const changes = Object.fromEntries(
      ROWS.filter((row) => !same(draft[row.key], plan.entitlements?.[row.key])).map((row) => [row.key, draft[row.key]])
    );
    const res = await savePlanEntitlements(plan.key, changes);
    setBusy("");
    if (res?.success) onSaved(res.msg || `${plan.name} updated.`);
    else onError(res?.msg || "Couldn't change the plan.");
  };

  const revert = async () => {
    setBusy("revert");
    const res = await revertPlanEntitlements(plan.key);
    setBusy("");
    if (res?.success) onSaved(res.msg || `${plan.name} is back to its defaults.`);
    else onError(res?.msg || "Couldn't revert the plan.");
  };

  const serviceOptions = services.services || [];

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose} PaperProps={dialogPaper}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>{plan.name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A change here applies to every workspace on this plan. To give one customer more, grant it on their workspace instead.
        </Typography>

        <Stack spacing={1.25}>
          {ROWS.map((row) => {
            const value = draft[row.key];
            const change = plan.changes?.[row.key];
            return (
              <Stack
                key={row.key}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  border: `1px solid ${change ? brand.signalDeep : brand.line}`,
                  bgcolor: change ? brand.markSoft : "#fff",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.label}
                  </Typography>
                  {change && (
                    <Typography variant="caption" color="text.secondary">
                      Shipped default: {row.format ? row.format(change.default) : change.default ? "included" : "not included"}
                    </Typography>
                  )}
                </Box>

                {row.kind === "flag" && <Switch size="small" checked={Boolean(value)} onChange={(e) => set(row.key, e.target.checked)} />}

                {row.kind === "number" && (
                  <TextField
                    size="small"
                    type="number"
                    value={value ?? 0}
                    onChange={(e) => set(row.key, Math.max(0, Number(e.target.value) || 0))}
                    sx={{ width: 120 }}
                    inputProps={{ min: 0 }}
                  />
                )}

                {row.kind === "choice" && (
                  <Select
                    size="small"
                    value={value ?? row.options[0].value}
                    onChange={(e) => set(row.key, e.target.value)}
                    sx={{ minWidth: 180 }}
                    MenuProps={menuPaper}
                  >
                    {row.options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}

                {row.kind === "services" && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" sx={{ maxWidth: "60%" }}>
                    {serviceOptions.map((option) => {
                      const on = Array.isArray(value) && value.includes(option.service);
                      return (
                        <Tooltip key={option.service} title={option.description || ""}>
                          <Chip
                            size="small"
                            label={option.title || option.service}
                            onClick={() =>
                              set(
                                row.key,
                                on
                                  ? (value || []).filter((name) => name !== option.service)
                                  : [...(value || []), option.service]
                              )
                            }
                            sx={{
                              fontWeight: 600,
                              borderRadius: 2,
                              bgcolor: on ? brand.ink : "transparent",
                              color: on ? "#fff" : "text.primary",
                              border: `1px solid ${on ? brand.ink : brand.line}`,
                              "&:hover": { bgcolor: on ? brand.inkSoft : brand.canvas },
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            );
          })}
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
          {services.source === "flowos"
            ? "Services listed by FlowOS. A service the FlowOS team deploys appears here on its own."
            : "FlowOS didn't answer, so the services this release knows about are offered."}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {changedHere > 0 && (
          <Button
            variant="outlined"
            startIcon={<RestartAltRoundedIcon />}
            disabled={Boolean(busy)}
            onClick={revert}
            sx={{ ...quietDanger, mr: "auto" }}
          >
            Back to defaults
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!dirty || Boolean(busy)}
          startIcon={busy === "save" ? <CircularProgress size={16} color="inherit" /> : null}
          onClick={save}
        >
          Save plan
        </Button>
      </DialogActions>
    </Dialog>
  );
}
